import gsap from 'gsap';
import * as PIXI from 'pixi.js';
import tinycolor from '../loaders/tinycolor2';
import MediaItem from './MediaItem';
import {
  BOTTOM_LAYER_ZINDEX,
  FOREGROUND_IMAGE_ZINDEX,
} from '../constants/Layers';
import Renderer from '../Renderer';
import Draggable from '../utils/Draggable/Draggable';

function getGlobalPosition(displayObject, scaleAdjust) {
  const globalBounds = displayObject.getBounds();
  return {
    x:
      globalBounds.x +
      (displayObject.pivot.x * displayObject.scale.x) / scaleAdjust,
    y:
      globalBounds.y +
      (displayObject.pivot.y * displayObject.scale.y) / scaleAdjust,
  };
}

function setGlobalPosition(displayObject, position, scaleAdjust) {
  const globalPosition = getGlobalPosition(displayObject, scaleAdjust);
  const positionOffset = {
    x: displayObject.x - globalPosition.x,
    y: displayObject.y - globalPosition.y,
  };
  displayObject.x = position.x + positionOffset.x;
  displayObject.y = position.y + positionOffset.y;
}

const MIN_SCALE = 0.05;

export default class BackgroundMedia extends MediaItem {
  constructor(model, app, sprite) {
    super(app, true);
    this._greensockTimeline = gsap.timeline();
    this.sprite = sprite;

    this.fadeIn = model.fadeIn ?? 0;
    this.fadeOut = model.fadeOut ?? 0;

    this.draggable = new Draggable({
      app,
      contents: this.sprite,
      anchor: null,
    });

    this.overlay = new PIXI.Graphics();

    this.updateModel(model);

    this.setupDraggable();
    this.container.addChild(
      this.sprite,
      this.overlay,
      this.draggable.container
    );
  }

  getDefaultZIndex() {
    return FOREGROUND_IMAGE_ZINDEX;
  }

  /** This function is used by the constructor, as well as Undo/Redo. */
  updateModel(model) {
    this.baseScale = this.app.screen.height / this.sprite.texture.height;

    // Scale before moving to make math easier
    this.handleScale(model.scale || 1);

    // Initialize position (important for some old templates)
    if (!model.position) {
      model.position = { x: 'center', y: 'center' };
    }

    // Place the draggable sprite
    const { x: originX, y: originY } = this.getAbsolutePosition(this.sprite, {
      horizontal: model.position.x,
      vertical: model.position.y,
    });
    this.sprite.x = originX;
    this.sprite.y = originY;
    this.updateDraggable();

    // Setup the tint to be used when rendering
    this.tint = null;
    if (model.filters?.tint) {
      const tint = model.filters.tint;
      const color = parseInt(`0x${tinycolor({ ...tint }).toHex()}`);
      this.tint = { color, alpha: tint.a };
    }

    super.updateModel(model);

    this.greensockTimeline.clear();

    const { fadeIn, fadeOut, opacity } = model;
    const isValidOpacity = !isNaN(parseFloat(opacity));
    if (isValidOpacity) {
      this.sprite.alpha = opacity;
    }
    if (fadeIn || fadeOut) {
      this.fade(fadeIn, fadeOut, isValidOpacity ? opacity : 1);
    }
  }

  render(play = false) {
    super.render(play);
    this.overlay.clear();
    if (this.tint) {
      this.overlay.beginFill(this.tint.color, this.tint.alpha);
      const spriteBounds = this.sprite.getBounds();
      this.overlay.drawRect(
        spriteBounds.x,
        spriteBounds.y,
        spriteBounds.width,
        spriteBounds.height
      );
      this.overlay.endFill();
    }
  }

  _onClick(event) {
    Renderer.emit(Renderer.CLICK_ITEM, this.model, event);
  }

  handleScale(scale) {
    this.sprite.scale.x = this._safeScale(scale * this.baseScale);
    this.sprite.scale.y = this._safeScale(scale * this.baseScale);
  }

  _safeScale(scale) {
    return Math.max(MIN_SCALE, scale);
  }

  handleMove(newLocalPosition) {
    // Ensure confined items do not escape the canvas on aspect ratio change
    const { x, y } = newLocalPosition;
    setGlobalPosition(this.sprite, { x, y }, this.baseScale);
  }

  _onDragEnd(wasMoved, wasScaled) {
    this.updateDraggable();

    const globalPosition = getGlobalPosition(this.sprite, this.baseScale);
    const { horizontal, vertical } = this.getRelativePosition(this.sprite, {
      x: globalPosition.x,
      y: globalPosition.y,
    });
    const newRelativePosition = {
      x: horizontal,
      y: vertical,
    };

    if (wasMoved) {
      Renderer.emit(Renderer.MOVE_ITEM, this.model, newRelativePosition);
    }
    if (wasScaled) {
      const scale = this.sprite.scale.x / this.baseScale;
      Renderer.emit(
        Renderer.SCALE_ITEM,
        this.model,
        scale,
        newRelativePosition
      );
    }
  }

  _onDragMove() {
    this.updateDraggable();
    this.render();
  }

  _onDragStart() {
    this.updateDraggable();
  }

  setupDraggable() {
    if (!this.app.isInteractive) this.draggable.turnOffInteraction();

    this.draggable.onStart = this._onDragStart.bind(this);
    this.draggable.onClick = this._onClick.bind(this);
    this.draggable.onDrag = this._onDragMove.bind(this);
    this.draggable.onEnd = this._onDragEnd.bind(this);
    this.draggable.safeScale = this._safeScale.bind(this);
  }

  updateDraggable() {
    this.draggable.update(this.sprite.getBounds());
  }

  fade(fadeIn, fadeOut, opacity) {
    this.fadeIn = fadeIn;
    this.fadeOut = fadeOut;

    if (fadeIn > 0) {
      this.greensockTimeline.fromTo(
        this.sprite,
        { alpha: 0 },
        { alpha: opacity, duration: fadeIn }
      );
    }

    if (fadeOut > 0) {
      this.greensockTimeline.to(
        this.sprite,
        { alpha: 0, duration: fadeOut },
        this.duration - fadeOut
      );
    }
  }

  get greensockTimeline() {
    return this._greensockTimeline;
  }
}
